import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { money } from "@/lib/format";
import {
  ChartLineUp, Buildings, Wallet, Receipt, TrendUp, TrendDown, CalendarCheck, FileText, Package, FilePdf, FileXls,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

function Kpi({ icon: Icon, label, value, tone, i }) {
  const toneCls = tone === "pos" ? "text-[hsl(var(--success))]" : tone === "neg" ? "text-destructive" : "text-primary";
  return (
    <Card className="rounded-sm border-border shadow-none p-6 fade-up hover:-translate-y-1 transition-transform bg-card"
      style={{ animationDelay: `${i * 60}ms` }} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground">{label}</span>
        <Icon size={20} className={toneCls} weight="regular" />
      </div>
      <div className={`font-mono text-2xl font-bold ${toneCls}`}>{value}</div>
    </Card>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [base, setBase] = useState(localStorage.getItem("ll-base") || "USD");
  const [currencies, setCurrencies] = useState([]);
  const nav = useNavigate();

  useEffect(() => { api.get("/currencies").then((r) => setCurrencies(r.data)); }, []);
  useEffect(() => {
    localStorage.setItem("ll-base", base);
    api.get("/dashboard", { params: { base } }).then((r) => setD(r.data));
  }, [base]);

  const downloadPortfolio = async () => {
    try {
      const res = await api.get("/portfolio/report", { params: { base }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `IFRS16-Portfolio-${base}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Portfolio report downloaded");
    } catch {
      toast.error("Could not generate portfolio report");
    }
  };

  const downloadPortfolioXlsx = async () => {
    try {
      const res = await api.get("/portfolio/report.xlsx", { params: { base }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = `IFRS16-Portfolio-${base}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Portfolio workbook downloaded");
    } catch {
      toast.error("Could not generate Excel workbook");
    }
  };

  if (!d) return <div className="text-muted-foreground text-sm">Loading dashboard…</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-2">IFRS 16 Overview</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">Dashboard</h1>
          <p className="text-xs text-muted-foreground mt-2">
            Totals converted to <span className="font-mono font-semibold text-foreground">{d.base_currency}</span> using live FX rates.
            {d.fx_missing?.length > 0 && <span className="text-amber-600"> No rate for {d.fx_missing.join(", ")} (shown unconverted).</span>}
            {!d.fx_available && <span className="text-amber-600"> Live rates unavailable — amounts summed as-is.</span>}
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div className="w-44">
            <label className="text-[10px] font-medium tracking-[0.15em] uppercase text-muted-foreground">Base Currency</label>
            <Select value={base} onValueChange={setBase}>
              <SelectTrigger className="mt-1.5 rounded-sm font-mono" data-testid="base-currency"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-64">
                {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={downloadPortfolio} className="rounded-sm gap-2" data-testid="portfolio-report-btn">
            <FilePdf size={16} /> Portfolio PDF
          </Button>
          <Button onClick={downloadPortfolioXlsx} variant="outline" className="rounded-sm gap-2" data-testid="portfolio-xlsx-btn">
            <FileXls size={16} /> Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi i={0} icon={FileText} label="Active Leases" value={`${d.active_leases} / ${d.total_leases}`} tone="pri" />
        <Kpi i={1} icon={TrendUp} label="Total ROU Assets" value={money(d.total_rou_asset, base)} tone="pos" />
        <Kpi i={2} icon={TrendDown} label="Lease Liabilities" value={money(d.total_lease_liability, base)} tone="neg" />
        <Kpi i={3} icon={Receipt} label="Other Expenses" value={money(d.total_expenses, base)} tone="neg" />
        <Kpi i={4} icon={ChartLineUp} label="Interest Expense" value={money(d.total_interest_expense, base)} tone="neg" />
        <Kpi i={5} icon={Wallet} label="Depreciation" value={money(d.total_depreciation, base)} tone="neg" />
        <Kpi i={6} icon={Package} label="Assets Tracked" value={d.total_assets} tone="pri" />
        <Kpi i={7} icon={Buildings} label="Lease Types" value={Object.keys(d.leases_by_type).length} tone="pri" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="rounded-sm border-border shadow-none p-6 fade-up bg-card" data-testid="cashflow-card">
          <div className="flex items-center gap-2 mb-5">
            <TrendUp size={18} className="text-primary" />
            <h2 className="font-display text-lg font-semibold tracking-tight">Cash Flow: Revenue vs Expense</h2>
          </div>
          {(() => {
            const rin = d.total_revenue_in || 0, eout = d.total_expense_out || 0;
            const mx = Math.max(rin, eout, 1);
            return (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-[hsl(var(--success))]">Money In (Revenue leases)</span>
                    <span className="font-mono" data-testid="revenue-in">{money(rin, base)}</span>
                  </div>
                  <div className="h-3 bg-secondary"><div className="h-full bg-[hsl(var(--success))]" style={{ width: `${(rin / mx) * 100}%` }} /></div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-destructive">Money Out (Expense leases)</span>
                    <span className="font-mono" data-testid="expense-out">{money(eout, base)}</span>
                  </div>
                  <div className="h-3 bg-secondary"><div className="h-full bg-destructive" style={{ width: `${(eout / mx) * 100}%` }} /></div>
                </div>
                <div className="flex justify-between text-sm pt-3 border-t border-border">
                  <span className="font-semibold">Net cash flow</span>
                  <span className={`font-mono font-bold ${(d.net_cash_flow || 0) >= 0 ? "text-[hsl(var(--success))]" : "text-destructive"}`} data-testid="net-cashflow">{money(d.net_cash_flow || 0, base)}</span>
                </div>
                <p className="text-[10px] text-muted-foreground">Based on total contractual lease payments over each lease term, converted to {base}.</p>
              </div>
            );
          })()}
        </Card>

        <Card className="rounded-sm border-border shadow-none p-6 fade-up bg-card">
          <div className="flex items-center gap-2 mb-5">
            <CalendarCheck size={18} className="text-primary" />
            <h2 className="font-display text-lg font-semibold tracking-tight">Upcoming Payments</h2>
          </div>
          {d.upcoming_payments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No upcoming payments scheduled.</p>
          ) : (
            <div className="space-y-1" data-testid="upcoming-list">
              {d.upcoming_payments.map((p, idx) => (
                <button key={idx} onClick={() => nav(`/leases/${p.lease_id}`)}
                  className="w-full flex items-center justify-between py-2.5 px-3 -mx-3 rounded-sm hover:bg-secondary transition-colors text-left">
                  <div>
                    <div className="text-sm font-medium">{p.lease_title}</div>
                    <div className="text-xs text-muted-foreground font-mono">{p.date}</div>
                  </div>
                  <span className="font-mono text-sm font-semibold">{money(p.amount, p.currency)}</span>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="rounded-sm border-border shadow-none p-6 fade-up bg-card">
          <h2 className="font-display text-lg font-semibold tracking-tight mb-5">Leases by Type</h2>
          {Object.keys(d.leases_by_type).length === 0 ? (
            <p className="text-sm text-muted-foreground">No leases yet.</p>
          ) : (
            <div className="space-y-3" data-testid="leases-by-type">
              {Object.entries(d.leases_by_type).map(([type, count]) => {
                const max = Math.max(...Object.values(d.leases_by_type));
                return (
                  <div key={type}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{type}</span>
                      <span className="font-mono text-muted-foreground">{count}</span>
                    </div>
                    <div className="h-2 bg-secondary">
                      <div className="h-full bg-primary transition-all" style={{ width: `${(count / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
